import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { db, auth } from '../firebase';
import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { toast } from 'react-toastify';

const DASHBOARD_LINKS = [
  {
    label: 'Firebase Console',
    href: 'https://console.firebase.google.com/project/spicify-eb1a8/overview',
    description: 'Crashlytics, Analytics, Auth, Firestore',
  },
  {
    label: 'Sentry',
    href: 'https://spicify-llc.sentry.io/',
    description: 'Website, admin, and backend errors',
  },
  {
    label: 'Stripe',
    href: 'https://dashboard.stripe.com/',
    description: 'Payments, subscriptions, webhooks',
  },
  {
    label: 'AdMob',
    href: 'https://admob.google.com/',
    description: 'Ad revenue, fill, requests, no-fill',
  },
  {
    label: 'UptimeRobot',
    href: 'https://dashboard.uptimerobot.com/monitors',
    description: 'Website, admin, and API uptime',
  },
];

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-800 border-green-200',
  past_due: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  canceled: 'bg-orange-100 text-orange-800 border-orange-200',
  expired: 'bg-red-100 text-red-800 border-red-200',
  free: 'bg-gray-100 text-gray-600 border-gray-200',
};

const STATUS_LABELS = {
  active: 'Active',
  past_due: 'Past Due',
  canceled: 'Canceled',
  expired: 'Expired',
  free: 'Free',
};

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value || 0);
}

function formatDate(value) {
  if (!value) return 'N/A';
  return value.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function normalizeDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const StatCard = ({ title, value, subtitle, tone = 'slate' }) => {
  const tones = {
    slate: 'border-gray-100',
    green: 'border-green-100',
    red: 'border-red-100',
    blue: 'border-blue-100',
    amber: 'border-amber-100',
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border p-6 ${tones[tone] || tones.slate}`}>
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
      {subtitle ? <p className="text-sm text-gray-400 mt-2">{subtitle}</p> : null}
    </div>
  );
};

const SectionCard = ({ title, subtitle, action, children }) => (
  <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {subtitle ? <p className="text-sm text-gray-500 mt-1">{subtitle}</p> : null}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const LinkCard = ({ label, href, description }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    className="block rounded-lg border border-gray-200 p-4 hover:border-gray-300 hover:shadow-sm transition"
  >
    <div className="flex items-center justify-between gap-3">
      <p className="font-medium text-gray-900">{label}</p>
      <span className="text-sm text-gray-400">Open</span>
    </div>
    <p className="text-sm text-gray-500 mt-2">{description}</p>
  </a>
);

const StatusBadge = ({ status, count }) => (
  <div className={`px-4 py-3 rounded-lg border ${STATUS_STYLES[status] || STATUS_STYLES.free} flex items-center justify-between`}>
    <span className="font-medium text-sm">{STATUS_LABELS[status] || status}</span>
    <span className="text-2xl font-bold">{count}</span>
  </div>
);

const EmptyState = ({ message }) => (
  <p className="text-sm text-gray-400">{message}</p>
);

const MiniStat = ({ label, value }) => (
  <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-xl font-bold text-gray-900 mt-1">{value}</p>
  </div>
);

export default function Analytics() {
  const [loading, setLoading] = useState(true);
  const [backendHealth, setBackendHealth] = useState(null);
  const [uptime, setUptime] = useState({ monitors: [], error: null });
  const [sentry, setSentry] = useState({ projects: [], error: null });
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeSubscribers: 0,
    pastDueUsers: 0,
    canceledUsers: 0,
    expiredUsers: 0,
    freeUsers: 0,
    totalRevenue: 0,
    recentPayments: [],
    subscriptionsByPlan: {},
    recentSignups: [],
    emailLogs: { sent: 0, failed: 0 },
    ads: {
      enabled: true,
      configUpdatedAt: null,
      disabledUsers: 0,
      enabledUsers: 0,
    },
    errors: {
      total: 0,
      last24Hours: 0,
      recent: [],
    },
    adStats: {
      requests: 0,
      loads: 0,
      impressions: 0,
      noFill: 0,
      revenue: 0,
      fillRate: 0,
      eCPM: 0,
      revPerUser: 0,
      noFillRate: 0,
    },
    readingStats: {
      bookOpens: 0,
      chaptersStarted: 0,
      chaptersCompleted: 0,
      completionRate: 0,
      dropOffRate: 0,
      activeReaders7d: 0,
      activeReaders30d: 0,
      sessions: 0,
    },
  });

  const fetchBackendHealth = useCallback(async () => {
    const apiBase = (process.env.REACT_APP_API_URL || 'https://api.spicifyapp.com').replace(/\/+$/, '');
    try {
      const response = await fetch(`${apiBase}/health`, { cache: 'no-store' });
      const data = await response.json();
      setBackendHealth({
        ok: response.ok && data.status === 'ok',
        ...data,
      });
    } catch (error) {
      console.error('Error fetching backend health:', error);
      setBackendHealth({
        ok: false,
        status: 'unreachable',
        error: error.message,
      });
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const backendHealthPromise = fetchBackendHealth();

      const usersSnap = await getDocs(collection(db, 'users'));
      const users = usersSnap.docs.map((docSnapshot) => ({
        id: docSnapshot.id,
        ...docSnapshot.data(),
      }));

      let activeSubscribers = 0;
      let pastDueUsers = 0;
      let canceledUsers = 0;
      let expiredUsers = 0;
      let freeUsers = 0;
      let adsDisabledUsers = 0;
      const subscriptionsByPlan = {};

      users.forEach((user) => {
        const status = user.subscription?.status;
        const planId = user.subscription?.planId;
        const plan = user.subscription?.plan;

        switch (status) {
          case 'active':
            activeSubscribers++;
            break;
          case 'past_due':
            pastDueUsers++;
            break;
          case 'canceled':
            canceledUsers++;
            break;
          case 'expired':
          case 'unpaid':
            expiredUsers++;
            break;
          default:
            freeUsers++;
            break;
        }

        if (user.adsRemoved === true || user.subscription?.status === 'active') {
          adsDisabledUsers++;
        }

        if (planId || plan) {
          const key = planId || plan;
          subscriptionsByPlan[key] = (subscriptionsByPlan[key] || 0) + 1;
        }
      });

      let recentPayments = [];
      try {
        const paymentsQuery = query(
          collection(db, 'payment_logs'),
          orderBy('timestamp', 'desc'),
          limit(10)
        );
        const paymentsSnap = await getDocs(paymentsQuery);
        recentPayments = paymentsSnap.docs.map((docSnapshot) => ({
          id: docSnapshot.id,
          ...docSnapshot.data(),
          timestamp: normalizeDate(docSnapshot.data().timestamp),
        }));
      } catch (error) {
        console.log('No payment_logs collection yet:', error.message);
      }

      const totalRevenueFromLogs = recentPayments
        .filter((payment) => payment.status === 'paid')
        .reduce((sum, payment) => sum + (payment.amount || 0), 0);

      let transactionRevenue = 0;
      try {
        const txQuery = query(
          collection(db, 'transactions'),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
        const txSnap = await getDocs(txQuery);
        txSnap.docs.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          if (data.paymentStatus === 'paid') {
            transactionRevenue += data.amountTotal || 0;
          }
        });
      } catch (error) {
        console.log('No transactions collection yet:', error.message);
      }

      let emailSent = 0;
      let emailFailed = 0;
      try {
        const sentQuery = query(collection(db, 'email_logs'), where('status', '==', 'sent'));
        const failedQuery = query(collection(db, 'email_logs'), where('status', '==', 'failed'));
        const [sentSnap, failedSnap] = await Promise.all([
          getCountFromServer(sentQuery),
          getCountFromServer(failedQuery),
        ]);
        emailSent = sentSnap.data().count;
        emailFailed = failedSnap.data().count;
      } catch (error) {
        console.log('No email_logs collection yet:', error.message);
      }

      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const recentSignups = users.filter((user) => {
        const createdAt = normalizeDate(user.createdAt);
        return createdAt && createdAt >= sevenDaysAgo;
      });

      let adsEnabled = true;
      let adsUpdatedAt = null;
      try {
        const adsConfig = await getDoc(doc(db, 'appConfig', 'ads'));
        if (adsConfig.exists()) {
          const data = adsConfig.data();
          adsEnabled = data.adsEnabled !== false;
          adsUpdatedAt = normalizeDate(data.updatedAt);
        }
      } catch (error) {
        console.log('No ads config yet:', error.message);
      }

      let totalErrors = 0;
      let recentErrors = [];
      let errorsLast24Hours = 0;
      try {
        const errorsCountSnap = await getCountFromServer(collection(db, 'app_errors'));
        totalErrors = errorsCountSnap.data().count;

        const errorsQuery = query(
          collection(db, 'app_errors'),
          orderBy('timestamp', 'desc'),
          limit(8)
        );
        const errorsSnap = await getDocs(errorsQuery);
        recentErrors = errorsSnap.docs.map((docSnapshot) => {
          const data = docSnapshot.data();
          return {
            id: docSnapshot.id,
            ...data,
            timestamp: normalizeDate(data.timestamp),
          };
        });

        const last24Hours = new Date(Date.now() - 24 * 60 * 60 * 1000);
        errorsLast24Hours = recentErrors.filter((entry) => entry.timestamp && entry.timestamp >= last24Hours).length;
      } catch (error) {
        console.log('No app_errors collection yet:', error.message);
      }

      let adStats = {
        requests: 0,
        loads: 0,
        impressions: 0,
        noFill: 0,
        revenue: 0,
        fillRate: 0,
        eCPM: 0,
        revPerUser: 0,
        noFillRate: 0,
      };
      let readingStats = {
        bookOpens: 0,
        chaptersStarted: 0,
        chaptersCompleted: 0,
        completionRate: 0,
        dropOffRate: 0,
        activeReaders7d: 0,
        activeReaders30d: 0,
        sessions: 0,
      };
      try {
        const eventsCutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        const events7d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        const eventsSnap = await getDocs(
          query(collection(db, 'analytics_events'), where('ts', '>=', eventsCutoff))
        );
        const readers7 = new Set();
        const readers30 = new Set();
        const sessionKeys = new Set();
        eventsSnap.docs.forEach((docSnapshot) => {
          const data = docSnapshot.data();
          const type = data.type;
          const ts = normalizeDate(data.ts);
          const uid = data.userId;
          if (ts) {
            if (ts >= events7d) readers7.add(uid);
            readers30.add(uid);
            if (uid) sessionKeys.add(`${uid}|${ts.toISOString().slice(0, 10)}`);
          }
          switch (type) {
            case 'ad_requested':
              adStats.requests++;
              break;
            case 'ad_loaded':
              adStats.loads++;
              break;
            case 'ad_shown':
              adStats.impressions++;
              break;
            case 'ad_no_fill':
              adStats.noFill++;
              break;
            case 'ad_impression':
              adStats.revenue += Number(data.value) || 0;
              break;
            case 'book_opened':
              readingStats.bookOpens++;
              break;
            case 'chapter_started':
              readingStats.chaptersStarted++;
              break;
            case 'chapter_completed':
              readingStats.chaptersCompleted++;
              break;
            default:
              break;
          }
        });
        adStats.fillRate = adStats.requests > 0 ? (adStats.loads / adStats.requests) * 100 : 0;
        adStats.eCPM = adStats.impressions > 0 ? (adStats.revenue / adStats.impressions) * 1000 : 0;
        adStats.revPerUser = users.length > 0 ? adStats.revenue / users.length : 0;
        adStats.noFillRate = adStats.requests > 0 ? (adStats.noFill / adStats.requests) * 100 : 0;
        readingStats.completionRate = readingStats.chaptersStarted > 0
          ? (readingStats.chaptersCompleted / readingStats.chaptersStarted) * 100
          : 0;
        readingStats.dropOffRate = 100 - readingStats.completionRate;
        readingStats.activeReaders7d = readers7.size;
        readingStats.activeReaders30d = readers30.size;
        readingStats.sessions = sessionKeys.size;
      } catch (error) {
        console.log('No analytics_events collection yet:', error.message);
      }

      const apiBase = (process.env.REACT_APP_API_URL || 'https://api.spicifyapp.com').replace(/\/+$/, '');
      let uptimeData = { monitors: [], error: null };
      let sentryData = { projects: [], error: null };
      try {
        const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        const headers = idToken ? { Authorization: `Bearer ${idToken}` } : {};
        const uRes = await fetch(`${apiBase}/api/analytics/uptime`, { headers });
        const uData = await uRes.json();
        if (uData.success && Array.isArray(uData.monitors)) {
          uptimeData.monitors = uData.monitors;
        } else {
          uptimeData.error = uData.error || 'Failed to load uptime';
        }
        const sRes = await fetch(`${apiBase}/api/analytics/sentry`, { headers });
        const sData = await sRes.json();
        if (sData.success && Array.isArray(sData.projects)) {
          sentryData.projects = sData.projects;
        } else {
          sentryData.error = sData.error || 'Failed to load Sentry';
        }
      } catch (error) {
        uptimeData.error = error.message;
        sentryData.error = error.message;
      }
      setUptime(uptimeData);
      setSentry(sentryData);

      await backendHealthPromise;

      setStats({
        totalUsers: users.length,
        activeSubscribers,
        pastDueUsers,
        canceledUsers,
        expiredUsers,
        freeUsers,
        totalRevenue: (totalRevenueFromLogs + transactionRevenue) / 100,
        recentPayments,
        subscriptionsByPlan,
        recentSignups,
        emailLogs: { sent: emailSent, failed: emailFailed },
        ads: {
          enabled: adsEnabled,
          configUpdatedAt: adsUpdatedAt,
          disabledUsers: adsDisabledUsers,
          enabledUsers: Math.max(users.length - adsDisabledUsers, 0),
        },
        errors: {
          total: totalErrors,
          last24Hours: errorsLast24Hours,
          recent: recentErrors,
        },
        adStats,
        readingStats,
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [fetchBackendHealth]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const conversionRate = useMemo(() => {
    if (!stats.totalUsers) return '0.0';
    return ((stats.activeSubscribers / stats.totalUsers) * 100).toFixed(1);
  }, [stats.activeSubscribers, stats.totalUsers]);

  const churnRate = useMemo(() => {
    const total = stats.activeSubscribers + stats.canceledUsers;
    if (!total) return '0.0';
    return ((stats.canceledUsers / total) * 100).toFixed(1);
  }, [stats.activeSubscribers, stats.canceledUsers]);

  const errorTone = stats.errors.last24Hours > 0 ? 'red' : 'green';
  const adsEnabledLabel = stats.ads.enabled ? 'Enabled' : 'Disabled';

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-500 mt-1">
            Monitoring view across users, subscriptions, revenue, ads, errors, notifications, and system health.
          </p>
        </div>
        <button
          onClick={fetchAnalytics}
          className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition flex items-center gap-2"
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-6">
        <StatCard
          title="Users"
          value={stats.totalUsers}
          subtitle={`${stats.recentSignups.length} new in last 7 days`}
          tone="blue"
        />
        <StatCard
          title="Active Subscribers"
          value={stats.activeSubscribers}
          subtitle={`${conversionRate}% conversion`}
          tone="green"
        />
        <StatCard
          title="Revenue"
          value={formatCurrency(stats.totalRevenue)}
          subtitle="From payment logs and transactions"
          tone="slate"
        />
        <StatCard
          title="Frontend Errors"
          value={stats.errors.total}
          subtitle={`${stats.errors.last24Hours} in last 24h`}
          tone={errorTone}
        />
        <StatCard
          title="Ads"
          value={adsEnabledLabel}
          subtitle={`${stats.ads.disabledUsers} users currently ad-free`}
          tone={stats.ads.enabled ? 'amber' : 'red'}
        />
      </div>

      <SectionCard
        title="Source Dashboards"
        subtitle="Client-owned systems used for production monitoring and diagnostics."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          {DASHBOARD_LINKS.map((link) => (
            <LinkCard key={link.label} {...link} />
          ))}
        </div>
      </SectionCard>

      <SectionCard
        title="System Health"
        subtitle="Backend availability and production integration status."
        action={
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            backendHealth?.ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {backendHealth?.ok ? 'Backend online' : 'Backend attention needed'}
          </span>
        }
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            ['API', backendHealth?.status || 'unknown'],
            ['Firebase', backendHealth?.checks?.firebaseConfig || 'unknown'],
            ['Stripe', backendHealth?.checks?.stripeConfig || 'unknown'],
            ['Email', backendHealth?.checks?.emailConfig || 'unknown'],
            ['Bunny CDN', backendHealth?.checks?.bunnyConfig || 'unknown'],
            ['Sentry', backendHealth?.checks?.sentryConfig || 'unknown'],
          ].map(([label, value]) => {
            const healthy = value === 'ok' || value === 'configured';
            return (
              <div
                key={label}
                className={`rounded-lg border p-3 ${
                  healthy ? 'bg-green-50 border-green-100' : 'bg-yellow-50 border-yellow-100'
                }`}
              >
                <p className="text-xs text-gray-500">{label}</p>
                <p className={`text-sm font-semibold mt-1 ${
                  healthy ? 'text-green-700' : 'text-yellow-700'
                }`}>
                  {value}
                </p>
              </div>
            );
          })}
        </div>
        {backendHealth?.timestamp ? (
          <p className="text-xs text-gray-400 mt-3">
            Last checked {new Date(backendHealth.timestamp).toLocaleString()}
          </p>
        ) : null}
      </SectionCard>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        <SectionCard title="Users" subtitle="Subscription status and acquisition snapshot.">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <StatusBadge status="active" count={stats.activeSubscribers} />
            <StatusBadge status="past_due" count={stats.pastDueUsers} />
            <StatusBadge status="canceled" count={stats.canceledUsers} />
            <StatusBadge status="expired" count={stats.expiredUsers} />
            <StatusBadge status="free" count={stats.freeUsers} />
          </div>

          <h3 className="text-sm font-semibold text-gray-900 mb-3">Recent Signups</h3>
          <div className="space-y-3">
            {stats.recentSignups.length === 0 ? (
              <EmptyState message="No signups in the last 7 days." />
            ) : (
              stats.recentSignups.slice(0, 6).map((user, index) => {
                const createdAt = normalizeDate(user.createdAt);
                return (
                  <div key={user.id || index} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{user.name || user.displayName || 'No name'}</p>
                      <p className="text-xs text-gray-400">{user.email || 'No email'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">{user.subscription?.status || 'free'}</p>
                      <p className="text-xs text-gray-400">{createdAt ? createdAt.toLocaleDateString('en-US') : 'N/A'}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </SectionCard>

        <SectionCard title="Subscriptions" subtitle="Plan mix and churn context.">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <p className="text-sm text-gray-500">Conversion Rate</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{conversionRate}%</p>
            </div>
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <p className="text-sm text-gray-500">Churn Rate</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{churnRate}%</p>
            </div>
          </div>

          {Object.keys(stats.subscriptionsByPlan).length === 0 ? (
            <EmptyState message="No plan distribution data yet." />
          ) : (
            <div className="space-y-3">
              {Object.entries(stats.subscriptionsByPlan).map(([planId, count]) => {
                const percentage = stats.activeSubscribers > 0
                  ? ((count / stats.activeSubscribers) * 100).toFixed(0)
                  : 0;

                return (
                  <div key={planId}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{planId}</span>
                      <span className="text-sm text-gray-500">{count} ({percentage}%)</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className="bg-red-500 h-2 rounded-full" style={{ width: `${Math.min(Number(percentage), 100)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Revenue" subtitle="Payments and transaction-based revenue snapshot.">
          <div className="mb-4 rounded-lg border border-gray-100 bg-gray-50 p-4">
            <p className="text-sm text-gray-500">Total Revenue Snapshot</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(stats.totalRevenue)}</p>
          </div>

          {stats.recentPayments.length === 0 ? (
            <EmptyState message="No payment activity yet." />
          ) : (
            <div className="space-y-3">
              {stats.recentPayments.slice(0, 6).map((payment) => (
                <div key={payment.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{payment.userId || 'Unknown user'}</p>
                    <p className="text-xs text-gray-400">{formatDate(payment.timestamp)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">
                      {formatCurrency((payment.amount || 0) / 100)}
                    </p>
                    <p className={`text-xs ${payment.status === 'paid' ? 'text-green-600' : 'text-red-600'}`}>
                      {payment.status || 'unknown'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        <SectionCard title="Ads" subtitle="Operational ad state and expected ad-free population.">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className={`rounded-lg border p-4 ${stats.ads.enabled ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}>
              <p className="text-sm text-gray-500">Global Ads Toggle</p>
              <p className={`text-xl font-bold mt-1 ${stats.ads.enabled ? 'text-green-700' : 'text-red-700'}`}>
                {adsEnabledLabel}
              </p>
            </div>
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <p className="text-sm text-gray-500">Users Without Ads</p>
              <p className="text-xl font-bold text-gray-900 mt-1">{stats.ads.disabledUsers}</p>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            This admin app does not persist ad event aggregates yet. Ad request/load/no-fill performance should be reviewed in Firebase Analytics and AdMob.
          </p>
          <p className="text-xs text-gray-400 mt-3">
            Last ads config update: {formatDate(stats.ads.configUpdatedAt)}
          </p>
        </SectionCard>

        <SectionCard title="Errors" subtitle="Frontend error logging plus Sentry as the primary source of truth.">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <p className="text-sm text-gray-500">Logged Frontend Errors</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.errors.total}</p>
            </div>
            <div className={`rounded-lg border p-4 ${stats.errors.last24Hours > 0 ? 'bg-red-50 border-red-100' : 'bg-green-50 border-green-100'}`}>
              <p className="text-sm text-gray-500">Last 24 Hours</p>
              <p className={`text-2xl font-bold mt-1 ${stats.errors.last24Hours > 0 ? 'text-red-700' : 'text-green-700'}`}>
                {stats.errors.last24Hours}
              </p>
            </div>
          </div>

          {stats.errors.recent.length === 0 ? (
            <EmptyState message="No recent frontend error logs found." />
          ) : (
            <div className="space-y-3">
              {stats.errors.recent.slice(0, 5).map((entry) => (
                <div key={entry.id} className="rounded-lg border border-gray-100 p-3">
                  <p className="text-sm font-medium text-gray-900">{entry.message || 'Unknown error'}</p>
                  <p className="text-xs text-gray-400 mt-1">{entry.location || 'Unknown location'}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatDate(entry.timestamp)}</p>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard title="Email & Notifications" subtitle="Delivery health from admin-triggered email logs.">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-green-50 rounded-lg p-4 text-center border border-green-100">
              <p className="text-3xl font-bold text-green-600">{stats.emailLogs.sent}</p>
              <p className="text-sm text-green-700 mt-1">Emails Sent</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4 text-center border border-red-100">
              <p className="text-3xl font-bold text-red-600">{stats.emailLogs.failed}</p>
              <p className="text-sm text-red-700 mt-1">Failures</p>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            Includes verification, welcome, payment-failure, and subscription notification sends recorded by the platform.
          </p>
        </SectionCard>
      </div>

      <SectionCard title="Ad Monetization" subtitle="In-app ad performance from mirrored events (last 30 days). AdMob remains authoritative for final settlement.">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          <MiniStat label="Requests" value={stats.adStats.requests} />
          <MiniStat label="Loads" value={stats.adStats.loads} />
          <MiniStat label="Impressions" value={stats.adStats.impressions} />
          <MiniStat label="No-Fill" value={stats.adStats.noFill} />
          <MiniStat label="Fill Rate" value={`${stats.adStats.fillRate.toFixed(1)}%`} />
          <MiniStat label="eCPM" value={formatCurrency(stats.adStats.eCPM)} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <p className="text-sm text-gray-500">Ad Revenue (30d)</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(stats.adStats.revenue)}</p>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <p className="text-sm text-gray-500">Revenue / User</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(stats.adStats.revPerUser)}</p>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <p className="text-sm text-gray-500">No-Fill Rate</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stats.adStats.noFillRate.toFixed(1)}%</p>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Revenue is tracked via impression-level onPaidEvent data from AdMob. Authoritative settlement numbers remain in the AdMob dashboard.
        </p>
      </SectionCard>

      <SectionCard title="Reading Engagement" subtitle="Chapter completion, drop-off, and active readers (last 30 days).">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          <MiniStat label="Book Opens" value={stats.readingStats.bookOpens} />
          <MiniStat label="Chapters Started" value={stats.readingStats.chaptersStarted} />
          <MiniStat label="Chapters Completed" value={stats.readingStats.chaptersCompleted} />
          <MiniStat label="Completion" value={`${stats.readingStats.completionRate.toFixed(1)}%`} />
          <MiniStat label="Drop-off" value={`${stats.readingStats.dropOffRate.toFixed(1)}%`} />
          <MiniStat label="Active Readers 30d" value={stats.readingStats.activeReaders30d} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <p className="text-sm text-gray-500">Active Readers (7d)</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stats.readingStats.activeReaders7d}</p>
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <p className="text-sm text-gray-500">Sessions (30d)</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stats.readingStats.sessions}</p>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Retention and precise session duration are best reviewed in Firebase Analytics (GA4), which tracks them automatically.
        </p>
      </SectionCard>

      <SectionCard title="Uptime & Availability" subtitle="Live status from UptimeRobot's external monitors.">
        {uptime.error ? (
          <EmptyState message={`Unable to load uptime: ${uptime.error}`} />
        ) : uptime.monitors.length === 0 ? (
          <EmptyState message="No monitors returned." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {uptime.monitors.map((m) => (
              <div
                key={m.id}
                className={`rounded-lg border p-4 ${m.status === 2 ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}
              >
                <p className="text-sm font-medium text-gray-900">{m.name}</p>
                <p className="text-xs text-gray-400 mt-0.5 truncate">{m.url}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className={`text-xs font-semibold ${m.status === 2 ? 'text-green-700' : 'text-red-700'}`}>
                    {m.statusText}
                  </span>
                  <span className="text-xs text-gray-500">{m.uptime30d != null ? `${m.uptime30d}% (30d)` : 'n/a'}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-gray-500">{m.responseTimeMs != null ? `${m.responseTimeMs}ms` : 'n/a'}</span>
                  <span className="text-xs text-gray-400">
                    {m.lastDowntime ? `last down ${new Date(m.lastDowntime).toLocaleString()}` : 'no recent downtime'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-400 mt-3">
          UptimeRobot sends downtime alerts directly to the account owner's email.
        </p>
      </SectionCard>

      <SectionCard title="Error Monitoring" subtitle="Unresolved issues and error volume from Sentry, per project.">
        {sentry.error ? (
          <EmptyState message={`Unable to load Sentry: ${sentry.error}`} />
        ) : sentry.projects.length === 0 ? (
          <EmptyState message="No project data returned." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {sentry.projects.map((p) => (
              <div key={p.slug} className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                <p className="text-sm font-medium text-gray-900">{p.slug}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-gray-500">Unresolved</span>
                  <span className="text-lg font-bold text-gray-900">{p.unresolved ?? '—'}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-gray-500">Events 24h</span>
                  <span className="text-sm font-semibold text-gray-900">{p.events24h ?? 0}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-gray-500">Events last hour</span>
                  <span className={`text-sm font-semibold ${(p.events1h || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {p.events1h ?? 0}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-400 mt-3">
          Sentry also emails the team when error volume spikes. Full traces are in the Sentry dashboard.
        </p>
      </SectionCard>
    </div>
  );
}
